import { _createFormFieldInput, BrandSymbol, Icon, Search, Select, SelectProps, useToast } from '@melio/penny';
import { EventProperties, useAnalytics } from '@melio/platform-analytics';
import { useMelioIntl } from '@melio/platform-i18n';
import { useConfig } from '@melio/platform-provider';
import { forwardRef, mergeRefs } from '@melio/platform-utils';
import { useEffect, useRef, useState } from 'react';

import { isBrandSymbol } from '../../../utils';
import { AddVendorModalWidget } from '../../AddVendor';
import { AddVendorFormFields, Managed } from '../../AddVendor/AddVendorForm/types';
import { useVendorSelectOptions } from './useVendorSelectOptions';

export type VendorSelectWidgetProps = Omit<SelectProps, 'options' | 'creatableOption' | 'emptyState'> & {
  eventContextName?: string;
  shouldClearField?: boolean;
  analyticsProperties?: EventProperties;
};

export const VendorSelectWidget = _createFormFieldInput(
  forwardRef<VendorSelectWidgetProps, 'input'>(
    ({ eventContextName, shouldClearField, analyticsProperties, onChange, placeholder, ...props }, propRef) => {
      const innerRef = useRef<HTMLInputElement>(null);

      const ref = mergeRefs([propRef, innerRef]);

      const [pendingCreationVendorName, setPendingCreationVendorName] = useState<string | null>();

      const {
        canSearchBusinesses,
        isLoading: isLoadingOptions,
        isDisabled,
        sections,
        createVendor,
        isCreatingVendor,
        handleSearch,
        getBusinessById,
        isMyVendor,
        getSelectedOptionBadgeType,
        getHasUnmanagedLocalVendorWithTheSameName,
        trackView,
        trackClickOption,
      } = useVendorSelectOptions({ eventContextName, isViewMode: props.isViewMode });

      const { track, trackMarketing } = useAnalytics();

      const { formatMessage } = useMelioIntl();

      type AddVendorModalState =
        | { isOpen: false }
        | {
            isOpen: true;
            managed?: Managed;
            defaultFormValues: {
              companyName: string;
            } & Partial<Pick<AddVendorFormFields, 'accountNumber' | 'confirmAccountNumber' | 'postalCode'>>;
          };

      const [addVendorModalState, setAddVendorModalState] = useState<AddVendorModalState>({
        isOpen: false,
      });

      const toast = useToast();

      const {
        vendor: {
          vendorSelect: {
            createVendor: { showVendorDetailsForm },
          },
        },
      } = useConfig().settings;

      const closeDropdown = () => {
        innerRef?.current?.blur();
      };

      const handleCreateOption = (inputValue: string) => {
        trackClickOption({ vendorType: 'new-local' });

        if (showVendorDetailsForm) {
          setAddVendorModalState({ isOpen: true, defaultFormValues: { companyName: inputValue } });
          closeDropdown();
          return;
        }

        // This is so we could display the name of the soon-to-be-created vendor as the placeholder.
        setPendingCreationVendorName(inputValue);

        createVendor({ name: inputValue })
          .then(({ id: createdVendorId }) => {
            toast({ type: 'success', title: formatMessage('addVendor.createVendorToast.success') });
            track('Vendor', 'Status', { vendorType: 'local', Status: 'success', ...analyticsProperties });
            trackMarketing('bill-create_create-vendor', { vendorType: 'local', Status: 'success' });
            onChange?.({ target: { value: createdVendorId } } as React.ChangeEvent<HTMLInputElement>);
          })
          .catch((error) => {
            setPendingCreationVendorName(null);
            toast({
              type: 'error',
              title: formatMessage('addVendor.createVendorToast.error', {
                companyName: inputValue,
              }),
            });
            track('Vendor', 'Status', { vendorType: 'local', Status: 'failure' });

            throw error;
          });
      };

      const getInputPlaceholder = () => {
        if (isCreatingVendor && pendingCreationVendorName) {
          return pendingCreationVendorName;
        }

        return placeholder;
      };

      const closeAddVendorModal = () => {
        setAddVendorModalState({ isOpen: false });
      };

      const clearField = () => {
        //setting "" and null alternately is a workaround - you can't put the same value twich
        onChange?.({ target: { value: props.value === '' ? null : '' } } as React.ChangeEvent<HTMLInputElement>);
      };

      useEffect(() => {
        if (shouldClearField) {
          clearField();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [shouldClearField]);

      const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const {
          target: { value },
        } = event;

        if (!value) {
          return clearField();
        }

        if (isMyVendor(value)) {
          trackClickOption({ vendorType: 'local', vendorId: value });

          return onChange?.(event);
        }

        const business = getBusinessById(value);
        if (business) {
          trackClickOption({ vendorType: 'directory', selectedBusiness: business });

          setAddVendorModalState({
            isOpen: true,
            defaultFormValues: {
              companyName: business.business.name,
            },
            managed: {
              self: business.business.self,
              managedBy: business.directoryName,
              isZipCodeNeeded: business.business.merchantZipRequired,
            },
          });
          closeDropdown();
          return;
        }
      };

      const selectedOptionBadgeType = getSelectedOptionBadgeType(props.value as string);

      const badgeElement =
        selectedOptionBadgeType &&
        (isBrandSymbol(selectedOptionBadgeType) ? (
          <BrandSymbol size="small" type={selectedOptionBadgeType} />
        ) : (
          <Icon size="small" type={selectedOptionBadgeType} color="brand" />
        ));

      const inputProps = {
        'data-component': 'VendorSelect',
        ref,
        ...props,
        options: sections,
        onChange: handleChange,
        onInputChange: handleSearch,
        onSearchTermReset: () => handleSearch(''),
        placeholder: getInputPlaceholder(),
        creatableOption: {
          label: (value: string) => formatMessage('form.vendorSelect.createLabel', { value }),
          onClick: handleCreateOption,
          shouldDisplay: (inputValue: string) =>
            Boolean(inputValue && !getHasUnmanagedLocalVendorWithTheSameName(inputValue)),
        },
        emptyState: { label: 'No options' },
        isLoading: isLoadingOptions,
        isDisabled: props.isDisabled || isDisabled,
        isViewMode: props.isViewMode,
        valueRightElement: badgeElement,
        onFocus: trackView,
      };

      return (
        <>
          {addVendorModalState.isOpen && (
            <AddVendorModalWidget
              isOpen={addVendorModalState.isOpen}
              onDone={(createdVendorId) => {
                onChange?.({ target: { value: createdVendorId } } as React.ChangeEvent<HTMLInputElement>);
                closeAddVendorModal();
              }}
              onClose={() => {
                clearField();
                closeAddVendorModal();
              }}
              onSwitchToUnmanagedForm={({ companyName, accountNumber, postalCode }) => {
                setAddVendorModalState({
                  ...addVendorModalState,
                  defaultFormValues: {
                    companyName,
                    accountNumber,
                    confirmAccountNumber: accountNumber,
                    postalCode,
                  },
                  managed: undefined,
                });
              }}
              defaultFormValues={addVendorModalState.defaultFormValues}
              managed={addVendorModalState.managed}
            />
          )}
          {canSearchBusinesses ? <Search shouldShowPresetOptions {...inputProps} /> : <Select {...inputProps} />}
        </>
      );
    }
  )
);

VendorSelectWidget.displayName = 'VendorSelectWidget';
